import { useCallback } from "react";
import { getProductRaw } from "@/api/client";
import type { ProductRawRow } from "@/api/types";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { UpcListInput } from "@/components/UpcListInput";
import { ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useDebouncedUrlFilter, useUrlListFilter } from "@/hooks/useUrlFilters";
import { formatCurrency, formatNumber } from "@/lib/format";

const columns: Column<ProductRawRow>[] = [
  { header: "DMA", cell: (r) => r.dma ?? "—", sortValue: (r) => r.dma },
  { header: "UPC", cell: (r) => r.upc, sortValue: (r) => r.upc },
  { header: "Brand", cell: (r) => r.brand ?? "—", sortValue: (r) => r.brand },
  {
    header: "Category",
    cell: (r) => r.subcategory ?? "—",
    sortValue: (r) => r.subcategory,
  },
  {
    header: "Product name",
    cell: (r) => r.productName ?? "—",
    sortValue: (r) => r.productName,
  },
  {
    header: "Lowest price",
    align: "right",
    cell: (r) => formatCurrency(r.minPrice),
    sortValue: (r) => r.minPrice,
  },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
  {
    header: "Sales",
    align: "right",
    cell: (r) => formatCurrency(r.amount),
    sortValue: (r) => r.amount,
  },
];

export function ProductRawPage() {
  const filters = useProductFilterOptions();
  const [upcsDraft, setUpcs, upcs] = useDebouncedUrlFilter("upcs");
  const [storeIds, setStoreIds, store] = useUrlListFilter("store");
  const [brandIds, setBrandIds, brand] = useUrlListFilter("brand");
  const [subcategoryIds, setSubcategoryIds, subcategory] =
    useUrlListFilter("subcategory");
  const [dmas, setDmas, dma] = useUrlListFilter("dma");

  // Every filter is a query param, so the row cap applies to the filtered
  // set — narrowing genuinely surfaces rows the unfiltered top 5,000 hid.
  const fetchRaw = useCallback(
    (token: string) =>
      getProductRaw(token, {
        storeId: store,
        brandId: brand,
        subcategoryId: subcategory,
        dma,
        upcs: upcs || undefined,
      }),
    [store, brand, subcategory, dma, upcs],
  );
  const state = useApiData(fetchRaw);
  const rows = state.status === "ready" ? state.data.rows : [];

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">Raw sales data</h1>
        <p className="text-sm text-muted-foreground">
          Per-UPC, per-DMA sales for the last 7 days.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <UpcListInput value={upcsDraft} onApply={setUpcs} />
        <MultiFilterSelect
          label="Store"
          allLabel="All stores"
          values={storeIds}
          onChange={setStoreIds}
          disabled={filters.disabled}
          options={filters.storeOptions}
        />
        <MultiFilterSelect
          label="Brand"
          allLabel="All brands"
          values={brandIds}
          onChange={setBrandIds}
          disabled={filters.disabled}
          options={filters.brandOptions}
        />
        <MultiFilterSelect
          label="Category"
          allLabel="All categories"
          values={subcategoryIds}
          onChange={setSubcategoryIds}
          disabled={filters.disabled}
          options={filters.categoryOptions}
        />
        <MultiFilterSelect
          label="DMA"
          allLabel="All DMAs"
          values={dmas}
          onChange={setDmas}
          disabled={filters.disabled}
          options={filters.dmaOptions}
        />
      </div>

      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>Raw data</CardTitle>
            {state.status === "ready" && state.data.capped ? (
              <CardDescription>
                Capped at the top {formatNumber(state.data.rowLimit)} rows by
                units — narrow the filters above to see the rest.
              </CardDescription>
            ) : null}
          </div>
          {state.status === "ready" ? (
            <CsvDownloadButton
              name="raw-product-data"
              columns={columns}
              rows={rows}
            />
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {state.status === "loading" ? <TableSkeleton rows={10} /> : null}
          {state.status === "error" ? (
            <ErrorState message={state.message} onRetry={state.reload} />
          ) : null}
          {state.status === "ready" ? (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(r, i) => `${r.upc}-${r.dma}-${i}`}
              maxHeight="36rem"
              emptyMessage="No sales match these filters"
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
